import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { productAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import MineralCalculator from '../components/MineralCalculator/MineralCalculator';
import NewsletterSignup from '../components/Newsletter/NewsletterSignup';
import SEO from '../components/SEO';

// Rotating philosophy quotes
const rotatingQuotes = [
    "Nature did the filtering. We did the sourcing.",
    "Pressure made it perfect.",
    "Minerals matter. The rest is just liquid.",
    "Not all water earns its minerals.",
    "More than hydration. It's composition.",
    "Respect the source.",
    "Real minerals. Real difference.",
    "Not just pure. Right."
];

// Helper to get localized text from product fields
const getLocalizedText = (field, language) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[language] || field['en'] || field['no'] || '';
};

const HomePage = () => {
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const { t, language } = useLanguage();
    const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentQuoteIndex((prev) => (prev + 1) % rotatingQuotes.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const fetchFeatured = async () => {
            try {
                const res = await productAPI.getAll({ featured: true });
                setFeaturedProducts(res.data.slice(0, 4));
            } catch (error) {
                console.error('Failed to fetch featured products:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchFeatured();
    }, []);

    return (
        <div className="page-transition" data-testid="home-page">
            <SEO 
                title={null}
                description="Norges første spesialiserte importør av premium mineralvann. Utforsk ROI, Vichy Catalan, Gerolsteiner og Marzia - håndplukket fra Europas beste kilder."
                canonical="/"
            />
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ 
                        backgroundImage: 'url(https://images.unsplash.com/photo-1767648342443-42c72f1fd4c2?w=1920&q=80)'
                    }}
                >
                    <div className="absolute inset-0 bg-stone-900/50" />
                </div>
                
                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-32">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl"
                    >
                        <p className="text-xs uppercase tracking-[0.3em] text-white/80 mb-6">
                            {t('heroSubtitle')}
                        </p>
                        <h1 className="font-display text-6xl md:text-8xl font-bold text-white leading-none mb-4 tracking-tight">
                            RAW MINERALS
                        </h1>
                        <h2 className="font-display text-3xl md:text-5xl font-light text-white/90 leading-tight italic">
                            {t('heroTitle')}
                        </h2>
                    </motion.div>
                </div>
            </section>

            {/* Philosophy Section */}
            <section className="py-32 bg-stone-50" data-testid="philosophy-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-24 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="font-display text-3xl md:text-4xl font-semibold mb-8">
                                {t('philosophyTitle')}
                            </h2>
                            <p className="text-stone-600 leading-relaxed mb-6">
                                {t('philosophyText1')}
                            </p>
                            <p className="text-stone-600 leading-relaxed mb-6">
                                {t('philosophyText2')}
                            </p>
                            <p className="text-stone-900 leading-relaxed font-medium">
                                {t('philosophyText3')}
                            </p>
                        </motion.div>
                        
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                            className="relative aspect-[4/5] overflow-hidden"
                        >
                            <img 
                                src="https://images.unsplash.com/photo-1548839140-29a749e1cf4d?w=1200&q=90"
                                alt="Water being poured into a glass"
                                className="w-full h-full object-cover"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Training & Recovery Section */}
            <section className="py-32 bg-stone-50" data-testid="training-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center">
                        {/* Text - shows first on mobile, second on desktop */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                            className="order-1 md:order-2"
                        >
                            <p className="caption mb-4">{t('trainingTitle')}</p>
                            <h2 className="font-display text-3xl md:text-4xl font-semibold mb-8">
                                {t('trainingHeading')}
                            </h2>
                            <p className="text-stone-600 leading-relaxed mb-6">
                                {t('trainingText1')}
                            </p>
                            <p className="text-stone-600 leading-relaxed mb-6">
                                {t('trainingText2')}
                            </p>
                            <p className="text-stone-600 leading-relaxed mb-6">
                                {t('trainingText3')}
                            </p>
                            <p className="text-stone-900 leading-relaxed font-medium">
                                {t('trainingText4')}
                            </p>
                        </motion.div>

                        {/* Image - shows second on mobile, first on desktop */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                            className="relative aspect-[4/5] overflow-hidden order-2 md:order-1"
                        >
                            <img 
                                src="https://images.unsplash.com/photo-1570125552327-324f95e79e72?w=1200&q=90" 
                                alt="Athlete drinking water during training"
                                className="w-full h-full object-cover"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Restaurant & Gastronomy Section */}
            <section className="py-32 bg-white" data-testid="gastronomy-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center">
                        {/* Text - left side */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <p className="caption mb-4">{t('gastronomyTitle')}</p>
                            <h2 className="font-display text-3xl md:text-4xl font-semibold mb-8">
                                {t('gastronomyHeading')}
                            </h2>
                            <p className="text-stone-600 leading-relaxed mb-6">
                                {t('gastronomyText1')}
                            </p>
                            <p className="text-stone-600 leading-relaxed mb-6">
                                {t('gastronomyText2')}
                            </p>
                            <p className="text-stone-600 leading-relaxed mb-6">
                                {t('gastronomyText3')}
                            </p>
                            <p className="text-stone-600 leading-relaxed mb-6">
                                {t('gastronomyText4')}
                            </p>
                            <p className="text-stone-900 leading-relaxed font-medium">
                                {t('gastronomyText5')}
                            </p>
                        </motion.div>

                        {/* Image - right side */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                            className="relative aspect-[4/5] overflow-hidden"
                        >
                            <img 
                                src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=90" 
                                alt="Fine dining restaurant table setting"
                                className="w-full h-full object-cover"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Health & Wellness Section */}
            <section className="py-32 bg-stone-50" data-testid="wellness-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center">
                        {/* Text - shows first on mobile, second on desktop */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                            className="order-1 md:order-2"
                        >
                            <p className="caption mb-4">{t('wellnessTitle')}</p>
                            <h2 className="font-display text-3xl md:text-4xl font-semibold mb-8">
                                {t('wellnessHeading')}
                            </h2>
                            <p className="text-stone-600 leading-relaxed mb-6">
                                {t('wellnessText1')}
                            </p>
                            <p className="text-stone-600 leading-relaxed mb-6">
                                {t('wellnessText2')}
                            </p>
                            <p className="text-stone-600 leading-relaxed mb-6">
                                {t('wellnessText3')}
                            </p>
                            <p className="text-stone-900 leading-relaxed font-medium">
                                {t('wellnessText4')}
                            </p>
                        </motion.div>

                        {/* Image - shows second on mobile, first on desktop */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                            className="relative aspect-[4/5] overflow-hidden order-2 md:order-1"
                        >
                            <img 
                                src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1200&q=90" 
                                alt="Peaceful wellness and meditation setting"
                                className="w-full h-full object-cover"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Mineral Calculator Section */}
            <MineralCalculator />

            {/* Featured Products */}
            <section className="py-32 bg-stone-50" data-testid="featured-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex justify-between items-end mb-16">
                        <div>
                            <p className="caption mb-4">{t('featuredSelection')}</p>
                            <h2 className="font-display text-4xl md:text-5xl font-light">
                                {t('curatedForYou')}
                            </h2>
                        </div>
                        <Link 
                            to="/products" 
                            className="hidden md:flex items-center gap-2 text-fjord text-sm uppercase tracking-wider hover:underline"
                            data-testid="view-all-link"
                        >
                            {t('viewAll')}
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="animate-pulse">
                                    <div className="relative w-full pb-[150%] bg-stone-200 mb-4 md:mb-6" />
                                    <div className="h-4 bg-stone-200 w-2/3 mb-2" />
                                    <div className="h-4 bg-stone-200 w-1/3" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
                            {featuredProducts.map((product, index) => (
                                <motion.div
                                    key={product.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <Link 
                                        to={`/products/${product.id}`}
                                        className="group block"
                                        data-testid={`featured-product-${index}`}
                                    >
                                        <div className="relative w-full pb-[150%] mb-3 md:mb-6 bg-gradient-to-b from-stone-100 to-stone-200 overflow-hidden">
                                            <div className="absolute inset-0 flex items-center justify-center p-3">
                                                <img 
                                                    src={product.image} 
                                                    alt={product.name}
                                                    className="max-w-[85%] max-h-[85%] object-contain transition-transform duration-700 group-hover:scale-105"
                                                    style={{ width: 'auto', height: 'auto' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                                            <div>
                                                <h3 className="font-display text-sm md:text-lg mb-1">{product.name}</h3>
                                                <p className="text-xs uppercase tracking-wider text-stone-500">{getLocalizedText(product.origin, language)}</p>
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    <div className="mt-12 text-center md:hidden">
                        <Link to="/products">
                            <Button variant="outline" className="rounded-none px-8 py-4">
                                {t('viewAll')}
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Newsletter Section */}
            <section id="newsletter" className="py-24 bg-stone-800" data-testid="newsletter-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <NewsletterSignup />
                    </motion.div>
                </div>
            </section>

            {/* Philosophy Section with Rotating Quotes */}
            <section className="py-32 bg-fjord text-white" data-testid="philosophy-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-8">
                            {t('ourPhilosophy')}
                        </p>
                        <div className="h-28 md:h-24 flex items-center justify-center mb-10">
                            <AnimatePresence mode="wait">
                                <motion.h2
                                    key={currentQuoteIndex}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -15 }}
                                    transition={{ duration: 0.8, ease: "easeInOut" }}
                                    className="font-display text-3xl md:text-5xl font-light italic"
                                >
                                    "{rotatingQuotes[currentQuoteIndex]}"
                                </motion.h2>
                            </AnimatePresence>
                        </div>
                        <Link to="/products">
                            <Button 
                                className="bg-white text-fjord hover:bg-white/90 rounded-none px-10 py-6 text-xs uppercase tracking-[0.2em] font-bold"
                                data-testid="cta-button"
                            >
                                {t('shopCollection')}
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
